import { Clock, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

type Props = {
  circleSize?: number;
  fontsize?: number;
  loading?: boolean;
  message?: string;
  subtitle?: string;
};

function LoadingBox({
  circleSize = 50,
  fontsize = 20,
  loading = true,
  message = 'جاري التحميل',
  subtitle = 'يرجى الانتظار...',
}: Props) {
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;

    if (loading) {
      timerId = setInterval(() => {
        setCounter((prevCounter) => prevCounter + 1);
      }, 1000);
    } else {
      setCounter(0);
    }

    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [loading]);

  return (
    <div
      className={cn(
        'relative flex h-full w-full flex-col items-center justify-center',
        'bg-white dark:bg-slate-900',
        'border border-blue-100 dark:border-blue-900/50',
        'rounded-xl shadow-lg',
        !loading && 'hidden',
      )}
    >
      {/* Enhanced background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_2px_2px,rgba(59,130,246,0.15)_1px,transparent_0)] bg-[length:24px_24px]"></div>
      </div>

      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/30 dark:from-blue-950/20 dark:via-transparent dark:to-indigo-950/20"></div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center p-8">
        {/* Enhanced loader */}
        <div className="relative mb-6">
          {/* Outer ring with animation */}
          <div className="absolute inset-0 animate-ping rounded-full border-2 border-blue-300 opacity-20 dark:border-blue-700"></div>

          {/* Main container */}
          <div className="relative">
            <div
              className="flex items-center justify-center rounded-full border-4 border-blue-100 bg-gradient-to-br from-white to-blue-50/50 shadow-xl dark:border-blue-800/50 dark:bg-gradient-to-br dark:from-slate-900 dark:to-blue-950/30"
              style={{
                width: `${circleSize + 20}px`,
                height: `${circleSize + 20}px`,
              }}
            >
              <Loader2
                className="animate-spin text-blue-600 dark:text-blue-400"
                size={circleSize}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
            </div>

            {/* Rotating accent ring */}
            <div
              className="absolute inset-0 animate-spin rounded-full border-r-2 border-t-2 border-blue-400 opacity-30 dark:border-blue-500"
              style={{ animationDuration: '3s' }}
            ></div>
          </div>
        </div>

        {/* Content section */}
        <div className="max-w-sm space-y-3 text-center">
          {/* Main heading */}
          <div className="space-y-1">
            <h3
              className="font-bold leading-tight text-slate-900 dark:text-white"
              style={{ fontSize: `${fontsize}px` }}
            >
              {message}
            </h3>

            {subtitle && (
              <p className="text-sm leading-relaxed text-blue-700 dark:text-blue-300">
                {subtitle}
              </p>
            )}
          </div>

          {/* Time indicator */}
          <div className="flex items-center justify-center space-x-2 space-x-reverse pt-1">
            <div className="flex items-center space-x-1 space-x-reverse rounded-full bg-blue-50 px-3 py-1 dark:bg-blue-950/50">
              <Clock className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                {counter} ثانية
              </span>
            </div>
          </div>
        </div>

        {/* Enhanced progress indicator */}
        <div className="mt-6 w-56">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
              التقدم
            </span>
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
              {Math.min((counter % 10) * 10, 100)}%
            </span>
          </div>

          <div className="h-2 w-full overflow-hidden rounded-full bg-blue-50 shadow-inner dark:bg-blue-950/50">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 shadow-sm transition-all duration-500 ease-out dark:from-blue-400 dark:via-blue-500 dark:to-indigo-500"
              style={{
                width: `${Math.min((counter % 10) * 10, 100)}%`,
              }}
            ></div>
          </div>
        </div>

        {/* Enhanced status indicators */}
        <div className="mt-5 flex items-center space-x-6 space-x-reverse">
          <div className="flex items-center space-x-1 space-x-reverse rounded-full bg-blue-50 px-2 py-1 dark:bg-blue-950/40">
            <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500 dark:bg-blue-400"></div>
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
              جاري المعالجة
            </span>
          </div>

          <div className="flex items-center space-x-1 space-x-reverse rounded-full bg-indigo-50 px-2 py-1 dark:bg-indigo-950/40">
            <div className="h-2 w-2 rounded-full bg-indigo-400 dark:bg-indigo-500"></div>
            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
              في الانتظار
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoadingBox;
